import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { compareIdentifiers } from '@persolog/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ImportType = 'profile' | 'questionnaire' | 'texts';

export interface PerLanguageCount {
    updated: number;
    added: number;
    unchanged: number;
}

export interface ImportResult {
    imported: number;
    updated: number;
    skipped: number;
    unmatchedLanguages: string[];
    /** Per-language change counts across ALL matched languages, not just the displayed ones. */
    perLanguage: Record<string, PerLanguageCount>;
    /** Identifiers that did not previously exist for this import type. */
    newIdentifiers: string[];
    /** Languages currently shown in the editing grid after the import. */
    displayedLanguages: string[];
    /** Matched languages whose imported cells contain U+FFFD (corrupted source). */
    encodingWarningLanguages: string[];
}

type RawDataEntry = Array<{ identifier: string; texts: Array<{ language: string; text: string }> }>;

interface RawDataContext {
    profile: RawDataEntry;
    questionnaire: RawDataEntry;
    default?: RawDataEntry;
}

interface CSVParseState {
    allRows: string[][];
    currentField: string;
    currentRecord: string[];
    inQuotes: boolean;
}

interface AvailableLanguage {
    code: string;
    name: string;
    selected: boolean;
    disabled: boolean;
}

interface LanguageMatch {
    csvColumn: string;
    index: number;
    matched: boolean;
    availableLanguage: AvailableLanguage | undefined;
}

/**
 * Applies one imported text change. Receives the maintenance item itself (not its
 * id) so the store records the change in O(1) without re-finding the item.
 */
type ApplyChange = (item: any, langCode: string, newText: string, importType: string) => void;

interface CSVRowContext {
    rowData: string[];
    identifier: string;
    matchedLanguages: LanguageMatch[];
    currentMaintenanceData: any;
    itemsByIdentifier: Map<string, any>;
    applyChange: ApplyChange;
    importType: string;
    rowIndex: number;
    rawIndex: RawIndex;
}

type CSVValidationResult =
    | { isValid: false; errorMessage: string }
    | { isValid: true; identifierIndex: number; languageColumns: Array<{ index: number; language: string }> };

// ─── String / Line-separator Helpers ─────────────────────────────────────────

function hasLineSeparators(text: string): boolean {
    return text.includes('\n') || text.includes('\r') ||
           text.includes('\u2028') || text.includes('\u2029');
}

function normalizeLineSeparators(text: string): string {
    return text
        .replace(/\u2028/g, '\n')
        .replace(/\u2029/g, '\n')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');
}

// ─── CSV Parsing ──────────────────────────────────────────────────────────────

function finalizeRecord(state: CSVParseState): void {
    state.currentRecord.push(state.currentField.trim());
    if (state.currentRecord.some(field => field.length > 0)) {
        state.allRows.push(state.currentRecord);
    }
    state.currentRecord = [];
    state.currentField = '';
}

function handleQuoteChar(state: CSVParseState, nextChar: string): number {
    if (state.inQuotes && nextChar === '"') {
        state.currentField += '"';
        return 2;
    }
    state.inQuotes = !state.inQuotes;
    return 1;
}

function handleNewlineChar(state: CSVParseState, char: string, nextChar: string): number {
    if (state.currentField || state.currentRecord.length > 0) {
        finalizeRecord(state);
    }
    return char === '\r' && nextChar === '\n' ? 2 : 1;
}

function parseCSVToRawRows(csvContent: string): string[][] {
    const state: CSVParseState = { allRows: [], currentField: '', currentRecord: [], inQuotes: false };
    let i = 0;

    while (i < csvContent.length) {
        const char = csvContent[i];
        const nextChar = csvContent[i + 1];

        if (char === '"') {
            i += handleQuoteChar(state, nextChar);
        } else if (char === ';' && !state.inQuotes) {
            state.currentRecord.push(state.currentField.trim());
            state.currentField = '';
            i++;
        } else if ((char === '\n' || char === '\r') && !state.inQuotes) {
            i += handleNewlineChar(state, char, nextChar);
        } else {
            state.currentField += char;
            i++;
        }
    }

    if (state.currentField || state.currentRecord.length > 0) {
        finalizeRecord(state);
    }

    return state.allRows;
}

function findHeaderRowIndex(rows: string[][]): number {
    const index = rows.findIndex(row =>
        row.some(cell => cell.toLowerCase().trim() === 'identifier')
    );
    if (index === -1) {
        console.warn('No header row with "identifier" column found');
    } else {
        console.log(`Found header row with 'identifier' at row ${index + 1}`);
    }
    return index;
}

function logParsingResults(allRows: string[][], result: string[][], skippedRows: number): void {
    console.log(`Raw parsing complete: ${allRows.length} rows found`);
    console.log(`Filtered to ${result.length} rows (skipped ${skippedRows} pre-header rows)`);
    if (result.length > 0) {
        console.log('Header columns:', result[0]);
        if (result.length > 1) {
            console.log('First data row sample:', result[1]?.slice(0, 3));
        }
    }
}

function parseCSVContent(csvContent: string): string[][] {
    console.log('Starting advanced CSV parsing...');
    const allRows = parseCSVToRawRows(csvContent);
    const headerRowIndex = findHeaderRowIndex(allRows);
    const result = headerRowIndex === -1 ? allRows : allRows.slice(headerRowIndex);
    logParsingResults(allRows, result, headerRowIndex === -1 ? 0 : headerRowIndex);
    return result;
}

// ─── CSV Header Validation ────────────────────────────────────────────────────

function extractLanguageColumns(headers: string[]): Array<{ index: number; language: string }> {
    return headers
        .map((language, index) => ({ index, language }))
        .filter(({ language }) => language !== 'identifier' && language.length > 0);
}

function validateAndParseCSVHeaders(csvContent: string): CSVValidationResult {
    const invalid = (errorMessage: string): CSVValidationResult => ({ isValid: false, errorMessage });

    const rows = parseCSVContent(csvContent);
    if (rows.length < 2) {
        console.log('Validation failed: Not enough rows', rows.length);
        return invalid('Format is invalid, file must have at least a header row and one data row');
    }

    const headers = rows[0].map(h => h.trim().toLowerCase());
    const identifierIndex = headers.indexOf('identifier');
    if (identifierIndex === -1) {
        console.log('Validation failed: No identifier column found');
        return invalid('Format is invalid, remember to have a column with identifier and at least one column with language');
    }

    const languageColumns = extractLanguageColumns(headers);
    console.log('Language columns found:', languageColumns);

    if (languageColumns.length === 0) {
        console.log('Validation failed: No language columns found');
        return invalid('Format is invalid, remember to have a column with identifier and at least one column with language');
    }

    console.log('Validation successful!');
    return { isValid: true, identifierIndex, languageColumns };
}

// ─── CSV Generation ───────────────────────────────────────────────────────────

function selectTextsByMode(
    profileTexts: any[],
    questionnaireTexts: any[],
    defaultTexts: any[],
    mode: ImportType
): any[] {
    const map = { profile: profileTexts, questionnaire: questionnaireTexts, texts: defaultTexts };
    return map[mode] ?? [];
}

function buildIdentifierMap(textsToProcess: any[], selectedLanguages: string[]): Map<string, Record<string, string>> {
    const allIdentifiers = new Map<string, Record<string, string>>();
    for (const item of textsToProcess) {
        if (!item.identifier || !item.texts) continue;
        const identifierTexts: Record<string, string> = {};
        for (const text of item.texts) {
            if (selectedLanguages.includes(text.language)) {
                identifierTexts[text.language] = text.text;
            }
        }
        if (Object.keys(identifierTexts).length > 0) {
            allIdentifiers.set(item.identifier, identifierTexts);
        }
    }
    return allIdentifiers;
}

function escapeCSVField(text: string): string {
    if (!text.includes(';') && !text.includes('"') && !hasLineSeparators(text)) {
        return text;
    }
    return `"${text.replace(/"/g, '""')}"`;
}

function buildCSVRow(identifier: string, texts: Record<string, string>, selectedLanguages: string[]): string[] {
    return [identifier, ...selectedLanguages.map(lang => escapeCSVField(texts[lang] || ''))];
}

export function generateCSVContentFromRawData(
    profileTexts: any[],
    questionnaireTexts: any[],
    defaultTexts: any[],
    selectedLanguages: string[],
    mode: ImportType
): string {
    const textsToProcess = selectTextsByMode(profileTexts, questionnaireTexts, defaultTexts, mode);
    console.log('📊 Texts to process:', textsToProcess.length, 'items');

    if (textsToProcess.length === 0) {
        console.warn('No texts available for processing');
        return '';
    }

    const allIdentifiers = buildIdentifierMap(textsToProcess, selectedLanguages);
    if (allIdentifiers.size === 0) return '';

    const headers = ['identifier', ...selectedLanguages];
    // Rows come in backend order; PE works through the file top to bottom, so natural identifier order.
    const rows = Array.from(allIdentifiers.entries())
        .sort(([a], [b]) => compareIdentifiers(a, b))
        .map(([identifier, texts]) => buildCSVRow(identifier, texts, selectedLanguages));

    return [headers, ...rows].map(row => row.join(';')).join('\n');
}

// ─── CSV Download ─────────────────────────────────────────────────────────────

function downloadCSV(content: string, filename: string, toast: any, t: any) {
    if (!content.trim()) {
        toast.add({
            severity: 'warn',
            summary: t('textMaintenance.csv.noData'),
            detail: t('textMaintenance.csv.noDataDetail'),
            life: 3000
        });
        return;
    }

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    toast.add({
        severity: 'success',
        summary: t('textMaintenance.csv.exportSuccess'),
        detail: t('textMaintenance.csv.exportSuccessDetail', { filename }),
        life: 3000
    });
}

// ─── Language Matching ────────────────────────────────────────────────────────

function processLanguageMatching(
    languageColumns: Array<{ index: number; language: string }>,
    availableLanguages: AvailableLanguage[]
): { matchedLanguages: LanguageMatch[]; unmatchedLanguages: LanguageMatch[] } {
    const languageMatches: LanguageMatch[] = languageColumns.map(langCol => {
        const availableLanguage = availableLanguages.find(
            availLang => availLang.code.toLowerCase() === langCol.language.toLowerCase()
        );
        return { csvColumn: langCol.language, index: langCol.index, matched: !!availableLanguage, availableLanguage };
    });

    const matchedLanguages = languageMatches.filter(m => m.matched);
    const unmatchedLanguages = languageMatches.filter(m => !m.matched);

    console.log('Language column matches:');
    for (const match of matchedLanguages) {
        console.log(`  "${match.csvColumn}" → matches available language "${match.availableLanguage!.code}" (${match.availableLanguage!.name})`);
    }
    if (unmatchedLanguages.length > 0) {
        console.log('❌ Language columns NOT found in available languages:');
        for (const mismatch of unmatchedLanguages) {
            console.log(`  ❌ "${mismatch.csvColumn}" → no match found`);
        }
    }

    return { matchedLanguages, unmatchedLanguages };
}

// ─── Raw Data Helpers ─────────────────────────────────────────────────────────

/** identifier → (language → text) lookup for the active import type. */
type RawIndex = Map<string, Record<string, string>>;

/**
 * Builds an O(1) identifier/language lookup once, so the per-cell "what is the
 * current backend text" check during import is O(1) instead of a linear scan of
 * the whole dataset (which made import O(rows × languages × identifiers)).
 */
function buildRawIndex(rawData: RawDataContext, importType: string): RawIndex {
    const dataMap: Record<string, RawDataEntry | undefined> = {
        profile: rawData.profile,
        questionnaire: rawData.questionnaire,
        texts: rawData.default
    };
    const source = dataMap[importType] ?? [];
    const index: RawIndex = new Map();
    for (const entry of source) {
        const langMap: Record<string, string> = {};
        for (const t of entry.texts) langMap[t.language] = t.text;
        index.set(entry.identifier, langMap);
    }
    return index;
}

function getCurrentTextFromRawData(identifier: string, language: string, rawIndex: RawIndex): string {
    return rawIndex.get(identifier)?.[language] ?? '';
}

// ─── Maintenance Item Helpers ─────────────────────────────────────────────────

function importTypeToPrefixMap(importType: string): string {
    const map: Record<string, string> = { questionnaire: 'questionnaire', texts: 'default' };
    return map[importType] ?? 'profile';
}

function createNewMaintenanceItem(
    identifier: string,
    importType: string,
    rowIndex: number,
    matchedLanguages: LanguageMatch[],
    rawIndex: RawIndex
): any {
    const prefix = importTypeToPrefixMap(importType);
    const newItem: any = {
        id: `${prefix}-${identifier}-${rowIndex}`,
        identifier,
        texts: {},
        originalTexts: {},
        changedTexts: {},
        isNew: true,
        newTexts: {}
    };
    for (const match of matchedLanguages) {
        const langCode = match.availableLanguage!.code;
        const backendText = getCurrentTextFromRawData(identifier, langCode, rawIndex);
        newItem.texts[langCode] = backendText;
        newItem.originalTexts[langCode] = backendText;
    }
    return newItem;
}

function applyLanguageUpdate(
    existingItem: any,
    match: LanguageMatch,
    rowData: string[],
    rawIndex: RawIndex,
    importType: string,
    applyChange: ApplyChange
): boolean {
    if (rowData.length <= match.index) return false;

    const newText = normalizeLineSeparators(rowData[match.index].trim());
    const langCode = match.availableLanguage!.code;
    const currentText = getCurrentTextFromRawData(existingItem.identifier, langCode, rawIndex);

    if (newText === currentText) {
        return false;
    }

    if (!existingItem.originalTexts[langCode]) {
        existingItem.originalTexts[langCode] = currentText;
    }
    // Pass the item itself so the store applies the change in O(1) (no re-find).
    applyChange(existingItem, langCode, newText, importType);
    return true;
}

// ─── CSV Row Processing ───────────────────────────────────────────────────────

function processCSVDataRow(ctx: CSVRowContext): { isNewItem: boolean; itemUpdated: boolean } {
    const { rowData, identifier, matchedLanguages, currentMaintenanceData, itemsByIdentifier, applyChange, importType, rowIndex, rawIndex } = ctx;

    let existingItem = itemsByIdentifier.get(identifier);
    let isNewItem = false;

    if (!existingItem) {
        const newItem = createNewMaintenanceItem(identifier, importType, rowIndex, matchedLanguages, rawIndex);
        currentMaintenanceData.items.push(newItem);
        itemsByIdentifier.set(identifier, newItem);
        existingItem = newItem;
        isNewItem = true;

        if (matchedLanguages.length > 0) {
            const firstLang = matchedLanguages[0].availableLanguage!.code;
            const currentText = getCurrentTextFromRawData(identifier, firstLang, rawIndex);
            applyChange(newItem, firstLang, currentText, importType);
        }
    }

    // Apply EVERY matched language. Using map (not some) is essential: `some`
    // short-circuits after the first changed language, which previously meant only
    // the first column (typically German) was ever imported per row.
    const updates = matchedLanguages.map(match =>
        applyLanguageUpdate(existingItem, match, rowData, rawIndex, importType, applyChange)
    );
    const itemUpdated = updates.some(Boolean);

    return { isNewItem, itemUpdated };
}

// ─── Language Auto-selection ──────────────────────────────────────────────────

function findLanguagesWithChanges(
    rows: string[][],
    identifierIndex: number,
    matchedLanguages: LanguageMatch[]
): Set<string> {
    const languagesWithChanges = new Set<string>();
    for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
        const rowData = rows[rowIndex];
        if (rowData.length <= identifierIndex) continue;
        for (const match of matchedLanguages) {
            if (rowData[match.index]?.trim()) {
                languagesWithChanges.add(match.availableLanguage!.code);
            }
        }
    }
    return languagesWithChanges;
}

function applyLanguageSelection(languageCodes: string[], availableLanguages: AvailableLanguage[]): string[] {
    const selected: string[] = [];
    for (const lang of availableLanguages) {
        if (languageCodes.includes(lang.code)) {
            lang.selected = true;
            lang.disabled = false;
            selected.push(lang.code);
        }
    }
    return selected;
}

function autoSelectLanguagesFromCSV(
    csvContent: string,
    validation: { identifierIndex: number; languageColumns: Array<{ index: number; language: string }> },
    matchedLanguages: LanguageMatch[],
    availableLanguages: AvailableLanguage[]
): string[] {
    const currentlySelected = availableLanguages.filter(lang => lang.selected);
    if (currentlySelected.length > 0) {
        return currentlySelected.map(l => l.code);
    }

    const rows = parseCSVContent(csvContent);
    const languagesWithChanges = findLanguagesWithChanges(rows, validation.identifierIndex, matchedLanguages);
    if (languagesWithChanges.size === 0) return [];

    const languagesToSelect = Array.from(languagesWithChanges).slice(0, 2);
    console.log('Auto-selecting languages (max 2):', languagesToSelect);
    return applyLanguageSelection(languagesToSelect, availableLanguages);
}

// ─── CSV Data Row Batch Processing ────────────────────────────────────────────

function processCSVDataRows(
    rows: string[][],
    validation: { identifierIndex: number },
    matchedLanguages: LanguageMatch[],
    currentMaintenanceData: any,
    applyChange: ApplyChange,
    importType: string,
    rawIndex: RawIndex
): { imported: number; updated: number } {
    let imported = 0;
    let updated = 0;

    // Build the identifier → item lookup once so each row is O(1), not O(items).
    const itemsByIdentifier = new Map<string, any>(
        currentMaintenanceData.items.map((item: any) => [item.identifier, item])
    );

    for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
        const rowData = rows[rowIndex];

        if (rowData.length <= validation.identifierIndex) {
            console.warn(`Skipping row ${rowIndex + 1}: insufficient columns`);
            continue;
        }

        const identifier = rowData[validation.identifierIndex].trim();
        if (!identifier) {
            console.warn(`Skipping row ${rowIndex + 1}: empty identifier`);
            continue;
        }

        const { isNewItem, itemUpdated } = processCSVDataRow({
            rowData, identifier, matchedLanguages, currentMaintenanceData,
            itemsByIdentifier, applyChange, importType, rowIndex, rawIndex
        });

        if (isNewItem) imported++;
        else if (itemUpdated) updated++;
    }

    return { imported, updated };
}

// ─── Per-language Import Summary ──────────────────────────────────────────────

interface ImportSummary {
    perLanguage: Record<string, PerLanguageCount>;
    newIdentifiers: string[];
    encodingWarningLanguages: string[];
}

/**
 * Classifies one matched CSV cell (added / updated / unchanged) against the backend cache
 * and records any encoding-corruption warning. Split out of buildImportSummary to keep the
 * per-row/per-language nesting readable.
 */
function summarizeCell(
    match: LanguageMatch,
    rowData: string[],
    identifier: string,
    isNewIdentifier: boolean,
    rawIndex: RawIndex,
    perLanguage: Record<string, PerLanguageCount>,
    encodingWarningLanguages: Set<string>
): void {
    if (rowData.length <= match.index) return;
    const langCode = match.availableLanguage!.code;
    const cell = normalizeLineSeparators(rowData[match.index].trim());

    if (cell.includes('�')) encodingWarningLanguages.add(langCode);

    const current = getCurrentTextFromRawData(identifier, langCode, rawIndex);
    if (isNewIdentifier) {
        if (cell !== '') perLanguage[langCode].added++;
    } else if (cell === current) {
        perLanguage[langCode].unchanged++;
    } else {
        perLanguage[langCode].updated++;
    }
}

/**
 * Builds a per-language change summary by comparing every matched CSV cell against
 * the raw (backend) cache. Runs across ALL matched languages so the result reflects
 * the full multi-language import, independent of which languages are displayed.
 */
function buildImportSummary(
    rows: string[][],
    identifierIndex: number,
    matchedLanguages: LanguageMatch[],
    rawIndex: RawIndex
): ImportSummary {
    const perLanguage: Record<string, PerLanguageCount> = {};
    for (const match of matchedLanguages) {
        perLanguage[match.availableLanguage!.code] = { updated: 0, added: 0, unchanged: 0 };
    }

    const newIdentifiers = new Set<string>();
    const encodingWarningLanguages = new Set<string>();

    for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
        const rowData = rows[rowIndex];
        if (rowData.length <= identifierIndex) continue;

        const identifier = rowData[identifierIndex].trim();
        if (!identifier) continue;

        const isNewIdentifier = !rawIndex.has(identifier);
        if (isNewIdentifier) newIdentifiers.add(identifier);

        for (const match of matchedLanguages) {
            summarizeCell(match, rowData, identifier, isNewIdentifier, rawIndex, perLanguage, encodingWarningLanguages);
        }
    }

    return {
        perLanguage,
        newIdentifiers: Array.from(newIdentifiers).sort(compareIdentifiers),
        encodingWarningLanguages: Array.from(encodingWarningLanguages)
    };
}

// ─── Public Import Orchestration ──────────────────────────────────────────────

export async function importCSVDataImpl(
    csvContent: string,
    availableLanguages: AvailableLanguage[],
    currentMaintenanceData: any,
    applyChange: ApplyChange,
    importType: ImportType,
    rawData: RawDataContext,
    processLanguageSelection: (languages: string[]) => Promise<void>
): Promise<ImportResult> {
    console.log('Starting CSV import...');

    const validation = validateAndParseCSVHeaders(csvContent);
    if (!validation.isValid) {
        throw new Error(validation.errorMessage || 'Invalid CSV format');
    }

    const { matchedLanguages, unmatchedLanguages } = processLanguageMatching(
        validation.languageColumns,
        availableLanguages
    );

    // Auto-selection governs DISPLAY only (the editing grid keeps its ≤2 cap).
    // Row processing below applies every matched language regardless of selection.
    const selectedLanguages = autoSelectLanguagesFromCSV(
        csvContent,
        { identifierIndex: validation.identifierIndex, languageColumns: validation.languageColumns },
        matchedLanguages,
        availableLanguages
    );

    if (selectedLanguages.length > 0) {
        await processLanguageSelection(selectedLanguages);
    }

    // Parse the CSV once and build the backend-text lookup once; both row
    // processing and the summary reuse them (O(rows × languages) overall).
    const rows = parseCSVContent(csvContent);
    const rawIndex = buildRawIndex(rawData, importType);

    const { imported, updated } = processCSVDataRows(
        rows,
        { identifierIndex: validation.identifierIndex },
        matchedLanguages,
        currentMaintenanceData,
        applyChange,
        importType,
        rawIndex
    );

    const summary = buildImportSummary(rows, validation.identifierIndex, matchedLanguages, rawIndex);

    console.log(`✅ Import completed: ${imported} new items, ${updated} updated items`);

    return {
        imported,
        updated,
        skipped: 0,
        unmatchedLanguages: unmatchedLanguages.map(m => m.csvColumn),
        perLanguage: summary.perLanguage,
        newIdentifiers: summary.newIdentifiers,
        displayedLanguages: availableLanguages.filter(lang => lang.selected).map(lang => lang.code),
        encodingWarningLanguages: summary.encodingWarningLanguages
    };
}

// ─── Composable Export ────────────────────────────────────────────────────────

export function useCSVOperations() {
    const { t } = useI18n();
    const toast = useToast();

    return {
        generateCSVContentFromRawData,
        downloadCSV: (content: string, filename: string) => downloadCSV(content, filename, toast, t),
        importCSVData: importCSVDataImpl
    };
}
